import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { MFAService } from '@app/pages/login-page/services/verify-totp.service';
import { ApiEndpoints } from '@app/utils/enums/api-endpoints';
import { LogInDataRequest, LogInResponse } from '@app/utils/interfaces/log-in.interface';
import { environment } from '@env/environment';
import {
  BehaviorSubject,
  catchError,
  filter,
  map,
  Observable,
  switchMap,
  take,
  tap,
  throwError,
} from 'rxjs';
import { MonitoringService } from '../monitoring/monitoring.service';

interface User {
  accessToken: string;
  refreshToken: string;
  tempToken: string;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = environment.apiBaseUrl;
  private readonly twoFactorService = inject(MFAService);
  private readonly monitoring = inject(MonitoringService);

  private readonly currentUser = signal<User | null>(null);
  private readonly refreshTokenSubject = new BehaviorSubject<string | null>(null);
  public readonly isLoggedIn = computed(() => Boolean(this.currentUser()));

  private isRefreshing = false;

  constructor() {
    const accessToken = this.getToken('accessToken');
    const refreshToken = this.getToken('refreshToken');

    if (accessToken && refreshToken) {
      this.setSession({ accessToken: accessToken, refreshToken: refreshToken, tempToken: '' });
    }
  }

  public login(data: LogInDataRequest): Observable<User> {
    return this.http.post<LogInResponse>(`${this.baseUrl}${ApiEndpoints.LOGIN}`, data).pipe(
      map((response) => {
        const is2FAEnabled = this.twoFactorService.determine2FAStatus(response);
        this.twoFactorService.update2FAStatus(is2FAEnabled);

        const user = response.data;
        this.updateTokens(user);

        this.monitoring.trackEvent('user_login', { timestamp: new Date().toISOString() });
        this.monitoring.setUser(user.accessToken);

        return user;
      }),
      catchError((error) => {
        return throwError(() => error);
      }),
    );
  }

  public logout(): void {
    this.currentUser.set(null);
    this.refreshTokenSubject.next(null);
    this.removeToken('accessToken');
    this.removeToken('refreshToken');
    this.removeToken('accessToken');
    this.removeToken('refreshToken');
  }

  public get currentUserValue(): User | null {
    return this.currentUser();
  }

  public setSession(user: User): void {
    this.currentUser.set(user);
  }

  public refreshToken(): Observable<string> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      const refreshToken = this.currentUserValue?.refreshToken;
      this.refreshTokenSubject.next(null);

      return this.http
        .post<LogInResponse>(`${this.baseUrl}${ApiEndpoints.REFRESH}`, {
          refreshToken,
        })
        .pipe(
          tap((response) => {
            const user = response.data;
            this.updateTokens(user);
            this.refreshTokenSubject.next(user.accessToken);
            this.isRefreshing = false;
          }),
          switchMap((response) => {
            return [response.data.accessToken];
          }),
        );
    } else {
      return this.refreshTokenSubject.pipe(
        filter((token) => token !== null),
        take(1),
      );
    }
  }

  public storeToken(token: string, value: string): void {
    localStorage.setItem(token, value);
  }

  public getToken(token: string): string | null {
    return localStorage.getItem(token);
  }

  public removeToken(token: string): void {
    localStorage.removeItem(token);
  }

  private updateTokens(user: User) {
    this.storeToken('accessToken', user.accessToken);
    this.storeToken('refreshToken', user.refreshToken);
    this.currentUser.set(user);
  }
}
